import { useState, useRef } from "react";
import { convertDate } from "@Services/luxonHelper";

const useFormValidator = (initialRules = {}) => {
  const [error, setError] = useState({});
  const rulesRef = useRef(initialRules);

  const updateRules = (newRules) => {
    rulesRef.current = { ...rulesRef.current, ...newRules };
  };

  const validate = ({ name, value }) => {
    const fieldRules = rulesRef.current[name] || {};
    rulesRef.current = {
      ...rulesRef.current,
      [name]: {
        ...rulesRef.current[name],
        value,
      },
    };

    if (fieldRules.required) {
      if (!value || (Array.isArray(value) && value.length === 0)) {
        setError((prevState) => ({
          ...prevState,
          [name]: "This field is required",
        }));
        return false;
      }
    }
    if (typeof value === "string" || typeof value === "number") {
      if (fieldRules.minLength && value?.length < fieldRules.minLength) {
        setError((prevState) => ({
          ...prevState,
          [name]: `Minimum ${fieldRules.minLength} characters required`,
        }));
        return false;
      } else if (fieldRules.maxLength && value?.length > fieldRules.maxLength) {
        setError((prevState) => ({
          ...prevState,
          [name]: `Maximum ${fieldRules.maxLength} characters allowed`,
        }));
        return false;
      } else if (fieldRules.minRange && Number(value) < fieldRules.minRange) {
        setError((prevState) => ({
          ...prevState,
          [name]: `Minimum value should be ${fieldRules.minRange}`,
        }));
        return false;
      } else if (fieldRules.maxRange && Number(value) > fieldRules.maxRange) {
        setError((prevState) => ({
          ...prevState,
          [name]: `Maximum value should be ${fieldRules.maxRange}`,
        }));
        return false;
      } else if (fieldRules.maxFieldRange && Number(value) > fieldRules.maxFieldRange) {
        setError((prevState) => ({
          ...prevState,
          [name]: `Maximum date can be ${convertDate(fieldRules.maxFieldRange, "dateforProjectDashbaord")}`,
        }));
        return false;
      } else if (fieldRules.minFieldRange && Number(value) < fieldRules.minFieldRange) {
        setError((prevState) => ({
          ...prevState,
          [name]: `Minimum date can be ${convertDate(fieldRules.minFieldRange, "dateforProjectDashbaord")}`,
        }));
        return false;
      } else if (fieldRules.selectionScope === "futureDateTime") {
        const currentDate = new Date();
        const selectedDate = new Date(value);
        if (selectedDate <= currentDate) {
          setError((prevState) => ({
            ...prevState,
            [name]: "Please select a future date and time",
          }));
          return false;
        }
        return true;
      } else if (fieldRules.selectionScope === "pastDateTime") {
        const currentDate = new Date();
        const selectedDate = new Date(value);
        if (selectedDate >= currentDate) {
          setError((prevState) => ({
            ...prevState,
            [name]: "Please select a past date and time",
          }));
          return false;
        }
        return true;
      }
    }
    if (fieldRules.minNoOfFiles && value?.length < fieldRules.minNoOfFiles) {
      setError((prevState) => ({
        ...prevState,
        [name]: `Minimum ${fieldRules.minNoOfFiles} files required`,
      }));
      return false;
    }
    if (fieldRules.maxNoOfFiles && value?.length > fieldRules.maxNoOfFiles) {
      setError((prevState) => ({
        ...prevState,
        [name]: `Maximum ${fieldRules.maxNoOfFiles} files allowed`,
      }));
      return false;
    }
    setError((prevState) => ({ ...prevState, [name]: "" }));
    return true;
  };

  function validateAll() {
    let isValid = true;
    Object.keys(rulesRef.current).forEach((name) => {
      const res = validate({ name, value: rulesRef.current[name].value });
      if (!res) {
        isValid = false;
      }
    });
    return isValid;
  }

  return { validate, error, updateRules, validateAll };
};

export default useFormValidator;
